import VideoJS from 'video.js';
import get from 'lodash/get';
import has from 'lodash/has';
import kebabCase from 'lodash/kebabCase';
import assign from 'lodash/assign';
import throttle from 'lodash/throttle';

import * as Controls from '../../ui/UIControlsNames';
import { secondsFromTimecode, serialize, assignDefined, timecodeFromSeconds } from '../../utils/ASVPUtils';
import {
    VIDEOS_SITE_URL,
    EMBED_CODE_L,
    EMBED_CODE_R,
    EMBED_URL,
    FACEBOOK_SHARE_URL,
    TWITTER_SHARE_URL,
    STREAMS_SITE_URL,
    LIVE_STREAM_ID
} from '../../constants';

const Component = VideoJS.getComponent('Component');

const COPY_TEXT = '&nbsp;Copy&nbsp;';
const HAS_CLIPBOARD = 'clipboard' in navigator && (typeof navigator.clipboard.writeText === 'function');
const HAS_EXEC_COMMAND = typeof document.execCommand === 'function';
const CAN_COPY = HAS_CLIPBOARD || HAS_EXEC_COMMAND;

// get the base path from the share config options if set, else use VIDEOS_SITE_URL
const getBasePath = (videoSource, options) => {
    if (has(options, 'share.basePath')) {
        return get(options, 'share.basePath');
    }

    if (videoSource.type === 'stream') {
        return STREAMS_SITE_URL;
    }

    return VIDEOS_SITE_URL;
};


// get the collection slug from the share config options if set, else use kebabCase(videoSource.collectionTitle)
const getCollectionSlug = (videoSource, options) => {
    if (has(options, 'share.collectionSlug')) {
        return get(options, 'share.collectionSlug');
    }

    if (videoSource.type === 'stream') {
        return '';
    }

    return kebabCase(videoSource.collectionTitle);
};

// get the video slug from the share config options if set, else use kebabCase(videoSource.title)
const getVideoSlug = (videoSource, options) => {
    if (has(options, 'share.videoSlug')) {
        return get(options, 'share.videoSlug');
    }

    if (videoSource.type === 'stream' && videoSource.id === LIVE_STREAM_ID) {
        return '';
    }

    return kebabCase(videoSource.title);
};

// the adultswim.com url of the video
const dataURL = (videoSource, options) => [
    getBasePath(videoSource, options),
    getCollectionSlug(videoSource, options),
    getVideoSlug(videoSource, options)
].filter(Boolean).join('/');

// set the custom share parameter (?s=//adultswim.com/special-url) if the share url override is set in the options (this is used by the embed route in /app to reinsert the url for future embeds)
const shareParam = (options) => get(options, 'share.url', undefined);

// set the time param as seconds (?t=53) if the timecode is defined
const timeParam = (timecode) => {
    if (!timecode) return undefined;
    return secondsFromTimecode(timecode);
};

// constructs the serialize params for the sharing
const getURLParams = (params) => {
    const result = serialize(assignDefined({}, { s: params.s, t: params.t }));
    return result ? `?${result}` : '';
};

// the final link to display on the share overlay (if share link override in options, use that, otherwise use dataURL)
const getDirectLink = (videoSource, options, timecode) => {
    const params = getURLParams({ t: timeParam(timecode) });
    const url = (has(options, 'share.url'))
        ? `${get(options, 'share.url')}${params}`
        : `${dataURL(videoSource, options)}${params}`;

    return url.replace(/\/$/, '');
};

// the embed code for the video
const getEmbedCode = (videoSource, options, timecode) => {
    const params = getURLParams({
        s: shareParam(options),
        t: timeParam(timecode)
    });
    return (videoSource.type === 'clip') ? `${EMBED_CODE_L}${EMBED_URL}${videoSource.id}${params}${EMBED_CODE_R}` : null;
};

// gets the timecode value from the current seconds of the player (if the type of video can support it)
const getTimecode = (videoSource, currentTime) => {
    if (videoSource.type === 'stream') return null;

    return timecodeFromSeconds(currentTime);
};


class ASShareOverlay extends Component {
    constructor(player, options) {
        super(player, options);

        this.copyButtons_ = this.el().querySelectorAll('.copy-button');
        this.socialButtons_ = this.el().querySelectorAll('a[data-user-interaction]');
        this.timeCheckbox_ = this.el().querySelector('#timecode-selection');
        this.timeCheckboxLabel_ = this.el().querySelector('.timecode-selection label');
        this.timeInput_ = this.el().querySelector('#timecode-input');
        this.timeText_ = this.el().querySelector('.timecode');
        this.embedCodeInput_ = this.el().querySelector('#embed-code-input');
        this.directLinkInput_ = this.el().querySelector('#direct-link-input');

        if (CAN_COPY) {
            this.copyButtons_.forEach(button => this.on(button, 'click', this.handleCopy));
        }

        this.socialButtons_.forEach(button => this.on(button, 'click', this.handleSocialClick));

        if (this.timeCheckbox_) {
            this.on(this.timeCheckbox_, 'change', this.handleTimecodeCheckboxChange);
            this.on(player, 'timeupdate', throttle(() => {
                if (!this.timeCheckbox_.checked) this.updateInputs();
            }, 100));
        }

        if (this.timeInput_) {
            this.on(this.timeInput_, 'keyup', this.handleTimecodeInputKeyup);
        }

        if (this.timeCheckboxLabel_) {
            this.on(this.timeCheckboxLabel_, 'keydown', this.handleTimecodeLabelKeydown);
        }
    }

    dispose() {
        super.dispose();
        this.copyButtons_ = null;
        this.socialButtons_ = null;
        this.timeCheckbox_ = null;
        this.timeCheckboxLabel_ = null;
        this.timeInput_ = null;
        this.timeText_ = null;
        this.embedCodeInput_ = null;
        this.directLinkInput_ = null;
    }

    createEl(_, props, attrs) {
        props = assign({
            innerHTML: this.content(),
            className: 'as-share-overlay'
        }, props);

        return super.createEl('div', props, attrs);
    }

    content() {
        const options = this.options_;
        const currentTime = this.player_.currentTime();
        const timecode = getTimecode(options.video, currentTime);
        const embedCode = getEmbedCode(options.video, options);
        const directLink = getDirectLink(options.video, options);

        return `
            <div class="share-overlay-content">
                <h2 class="share-heading">Share</h2>

                <ul class="share-icons">
                    <li class="facebook-icon">
                        <a href="${FACEBOOK_SHARE_URL}${directLink}" data-user-interaction="click:share:facebook" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 27.6 27.6" enable-background="new 0 0 27.6 27.6" xml:space="preserve" aria-hidden="true">
                            <path d="M13.8,27.6c7.6,0,13.8-6.2,13.8-13.8S21.4,0,13.8,0C6.2,0,0,6.2,0,13.8S6.2,27.6,13.8,27.6z M9.6,11.7h2.5
                                V9.4c0,0-0.1-1.5,1-2.6c1.1-1.1,2.6-1,4.8-0.8v2.5h-1.6c0,0-0.7,0-1,0.3c-0.3,0.4-0.3,0.9-0.3,1v1.8h2.8l-0.4,2.8h-2.4v7.3h-2.9
                                v-7.3H9.6V11.7z"/>
                            </svg>
                        </a>
                    </li>
                    <li class="twitter-icon">
                        <a href="${TWITTER_SHARE_URL}${directLink}" data-user-interaction="click:share:twitter" target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 27.6 27.6" enable-background="new 0 0 27.6 27.6" xml:space="preserve" aria-hidden="true">
                            <path d="M13.8,27.6c7.6,0,13.8-6.2,13.8-13.8S21.4,0,13.8,0S0,6.2,0,13.8S6.2,27.6,13.8,27.6z M10.5,17.5
                                c0,0-2.4,0-3.1-2.3c0,0,1.2,0.1,1.4-0.1c0,0-2.6-0.6-2.6-3.3c0,0,0.8,0.4,1.5,0.4c0,0-2.5-1.8-1-4.5c0,0,2.5,3.3,6.8,3.5
                                c0,0-0.6-2,1.2-3.5c1.8-1.5,4-0.2,4.5,0.3c0,0,1.3-0.2,2.2-0.8c0,0-0.3,1.1-1.4,1.8c0,0,1.3-0.2,1.8-0.5c0,0-0.6,1-1.7,1.7
                                c0,0,0.4,4.1-3.1,7.5c-3.6,3.4-9.4,2.7-11.5,0.9C5.7,18.9,8.5,19.2,10.5,17.5z"/>
                            </svg>
                        </a>
                    </li>
                </ul>

                <div class="copy-field">
                    <label for="direct-link-input">Direct Link:</label>
                    <div class="copy-input-wrapper">
                        <input id="direct-link-input" type="text" value="${directLink}" onclick="this.setSelectionRange(0, this.value.length);" readonly />
                        ${ CAN_COPY ? `<button class="copy-button" data-copy-target="#direct-link-input">${COPY_TEXT}</button>` : '' }
                    </div>
                </div>

                ${ !!embedCode ? `
                <div class="copy-field">
                    <label for="embed-code-input">Embed:</label>
                    <div class="copy-input-wrapper">
                        <input id="embed-code-input" type="text" value="${embedCode}" onclick="this.setSelectionRange(0, this.value.length);" readonly />
                        ${ CAN_COPY ? `<button class="copy-button" data-copy-target="#embed-code-input">${COPY_TEXT}</button>` : '' }
                    </div>
                </div>` : '' }

                ${ !!timecode ? `
                <div class="timecode-selection">
                    <div class="checkbox-wrapper">
                        <input type="checkbox" id="timecode-selection" />
                        <label tabindex="0" for="timecode-selection">Start at</label>
                        <input type="text" id="timecode-input" value="${timecode}" readonly />
                    </div>
                </div>` : '' }

            </div>
        `;
    }

    updateInputs(currentTime) {
        if (!this.timeCheckbox_) {
            return;
        }

        if (typeof currentTime === 'undefined') {
            currentTime = timecodeFromSeconds(this.player_.currentTime());
        }

        const options = this.options_;
        const { checked } = this.timeCheckbox_;
        const directLink = getDirectLink(options.video, options, checked ? currentTime : undefined);

        if (this.embedCodeInput_) {
            const embedCode = getEmbedCode(options.video, options, checked ? currentTime : undefined);
            this.embedCodeInput_.value = embedCode;
        }

        if (this.directLinkInput_) {
            this.directLinkInput_.value = directLink;
        }

        if (this.timeInput_ && !checked) {
            this.timeInput_.value = currentTime;
        }

        this.socialButtons_.forEach(a => {
            const href = a.getAttribute('href').split('=')[0];
            a.setAttribute('href', `${href}=${directLink}`);
        });
    }

    handleCopy(event) {
        if (!CAN_COPY) {
            return;
        }

        const button = event.target;
        const input = this.el().querySelector(button.dataset.copyTarget);

        if (!input || !input.value) {
            return;
        }

        input.focus();
        input.select();

        const done = () => {
            button.innerHTML = 'Copied';
            setTimeout(() => {
                try { button.innerHTML = COPY_TEXT; } catch(e) {}
            }, 2000);
        };

        if (HAS_CLIPBOARD) {
            navigator.clipboard.writeText(input.value).then(done).catch(() => {});
        } else if (HAS_EXEC_COMMAND) {
            if (document.execCommand('copy') === true) {
                done();
            }
        }
    }

    handleSocialClick(event) {
        this.trigger('userInteraction', event.currentTarget.dataset.userInteraction);
    }

    handleTimecodeCheckboxChange() {
        if (!this.timeCheckbox_) {
            return;
        }

        if (this.timeInput_) {
            this.timeInput_.readOnly = !this.timeCheckbox_.checked;
            this.timeInput_.value = timecodeFromSeconds(this.player_.currentTime());
        }

        this.updateInputs();
    }

    handleTimecodeInputKeyup(event) {
        const value = event.target.value;
        event.target.value = value.replace(/[^0-9\:]+/, '');
        this.updateInputs(value);
    }

    handleTimecodeLabelKeydown(event) {
        if (event.key === ' ' || event.key === 'Enter') {
            event.target.click();
        }
    }
}

VideoJS.registerComponent(Controls.SHARE_OVERLAY, ASShareOverlay);

export default ASShareOverlay;



// WEBPACK FOOTER //
// ./src/player/components/ASShareOverlay.js